import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import {
  auth,
  signInWithGoogle,
  signOut as fbSignOut,
  sendSignInLink,
  isEmailSignInLink,
  getStoredEmailForSignIn,
  clearStoredEmailForSignIn,
  completeEmailLinkSignIn,
} from '../firebase';

export type AuthReason = 'not_approved' | 'verify_failed' | null;
export type EmailLinkState = 'idle' | 'processing' | 'needs_email' | 'error';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  authError: string | null;
  /** Staff flag from /api/me. UX ONLY — server re-derives + enforces. */
  isStaff: boolean | null;
  /**
   * Whether the signed-in user is authorized for ANY access (staff or
   * allowlisted). `null` = unknown/loading. UX ONLY — every protected request is
   * independently enforced server-side; this just decides what to render.
   */
  isAuthorized: boolean | null;
  /** Why isAuthorized===false: 'not_approved' (403 not_allowlisted) vs 'verify_failed' (network/503/etc). */
  authReason: AuthReason;
  profileLoading: boolean;
  /** Top-level magic-link completion state (blocks gated rendering while processing). */
  emailLinkState: EmailLinkState;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
  /** Request a passwordless sign-in link to `email`. */
  sendLink: (email: string) => Promise<void>;
  /** Complete a magic-link sign-in when the email wasn't in localStorage (cross-device). */
  submitEmailForLink: (email: string) => Promise<void>;
  /** Re-run the /api/me access check for the current user. */
  retryAuth: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function cleanUrl(): void {
  try {
    window.history.replaceState(null, '', window.location.pathname);
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isStaff, setIsStaff] = useState<boolean | null>(null);
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const [authReason, setAuthReason] = useState<AuthReason>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [emailLinkState, setEmailLinkState] = useState<EmailLinkState>(() =>
    typeof window !== 'undefined' && isEmailSignInLink(window.location.href) ? 'processing' : 'idle',
  );

  // Each auth event / logout / retry bumps this so a late /api/me response from a
  // previous user can never apply after sign-out, a user switch, or link completion.
  const genRef = useRef(0);
  const emailLinkHandledRef = useRef(false);
  const emailLinkHrefRef = useRef<string | null>(null);

  // Fetch the access profile from /api/me; fail CLOSED on any non-OK / error.
  const fetchProfile = useCallback(async (u: User, gen: number) => {
    setProfileLoading(true);
    setIsStaff(null);
    setIsAuthorized(null);
    setAuthReason(null);
    try {
      const token = await u.getIdToken(/* forceRefresh */ false);
      if (!token) throw new Error('no token');
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 10_000);
      let res: Response;
      try {
        res = await fetch('/api/me', {
          headers: { Authorization: `Bearer ${token}` },
          credentials: 'omit',
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timer);
      }
      if (gen !== genRef.current) return;
      if (res.ok) {
        const data = (await res.json()) as { isStaff?: boolean };
        if (gen !== genRef.current) return;
        setIsStaff(data?.isStaff === true);
        setIsAuthorized(true);
        setAuthReason(null);
      } else {
        let reason: AuthReason = 'verify_failed';
        if (res.status === 403) {
          try {
            const j = (await res.json()) as { error?: string };
            if (j?.error === 'not_allowlisted') reason = 'not_approved';
          } catch {
            /* keep verify_failed */
          }
        }
        if (gen !== genRef.current) return;
        setIsStaff(false);
        setIsAuthorized(false);
        setAuthReason(reason);
      }
    } catch {
      if (gen !== genRef.current) return;
      setIsStaff(false);
      setIsAuthorized(false);
      setAuthReason('verify_failed'); // fail closed
    } finally {
      if (gen === genRef.current) setProfileLoading(false);
    }
  }, []);

  // Complete a magic-link sign-in (auto or cross-device). Fail-closed: invalidate
  // any prior authorized session up front so no stale gated UI renders during the
  // handoff, and ALWAYS clear the stored email + URL after the attempt (so a failed
  // attempt can't poison the next link).
  const runCompletion = useCallback(async (email: string, href: string) => {
    genRef.current++;
    setIsAuthorized(null);
    setProfileLoading(true);
    setEmailLinkState('processing');
    try {
      await completeEmailLinkSignIn(email, href);
      setEmailLinkState('idle'); // onAuthStateChanged → fetchProfile (new gen) resolves auth
    } catch {
      setEmailLinkState('error');
      const cur = auth.currentUser;
      if (cur) {
        const g = ++genRef.current;
        void fetchProfile(cur, g); // restore the still-signed-in user's access
      } else {
        setProfileLoading(false);
        setIsAuthorized(false);
      }
    } finally {
      clearStoredEmailForSignIn();
      cleanUrl();
    }
  }, [fetchProfile]);

  // Track auth state + fetch the access profile on each signed-in user.
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      const gen = ++genRef.current;
      setUser(u);
      setLoading(false);
      if (!u) {
        setIsStaff(false);
        setIsAuthorized(false);
        setAuthReason(null);
        setProfileLoading(false);
        return;
      }
      void fetchProfile(u, gen);
    });
    return () => {
      genRef.current++;
      unsub();
    };
  }, [fetchProfile]);

  // On mount, complete a magic-link sign-in if the page was opened from one. Runs
  // once per page load (emailLinkHandledRef); email links are full navigations so
  // a new link re-mounts with a fresh ref.
  useEffect(() => {
    if (emailLinkHandledRef.current) return;
    if (!isEmailSignInLink(window.location.href)) return;
    emailLinkHandledRef.current = true;
    emailLinkHrefRef.current = window.location.href;
    const stored = getStoredEmailForSignIn();
    if (!stored) {
      // Cross-device: the email isn't on this device — prompt for it. Keep the
      // URL (we still need its oobCode) until the user submits.
      setEmailLinkState('needs_email');
      return;
    }
    void runCompletion(stored, emailLinkHrefRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = useCallback(async () => {
    setAuthError(null);
    try {
      await signInWithGoogle();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign-in failed.';
      setAuthError(message);
      throw err;
    }
  }, []);

  const sendLink = useCallback(async (email: string) => {
    setAuthError(null);
    await sendSignInLink(email);
  }, []);

  const submitEmailForLink = useCallback(async (email: string) => {
    const href = emailLinkHrefRef.current;
    if (!href) {
      setEmailLinkState('error');
      return;
    }
    await runCompletion(email, href);
  }, [runCompletion]);

  const retryAuth = useCallback(() => {
    const u = auth.currentUser;
    if (!u) return;
    const gen = ++genRef.current;
    void fetchProfile(u, gen);
  }, [fetchProfile]);

  const logout = useCallback(async () => {
    genRef.current++; // invalidate any in-flight profile fetch
    setIsStaff(false);
    setIsAuthorized(false);
    setAuthReason(null);
    setProfileLoading(false);
    clearStoredEmailForSignIn();
    await fbSignOut();
    setAuthError(null);
  }, []);

  const getIdToken = useCallback(async () => {
    if (!auth.currentUser) return null;
    return auth.currentUser.getIdToken(/* forceRefresh */ false);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user, loading, authError, isStaff, isAuthorized, authReason, profileLoading,
      emailLinkState, login, logout, getIdToken, sendLink, submitEmailForLink, retryAuth,
    }),
    [user, loading, authError, isStaff, isAuthorized, authReason, profileLoading,
      emailLinkState, login, logout, getIdToken, sendLink, submitEmailForLink, retryAuth],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
